import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';

// We want these to match the API schema as closely as possible

export const azureJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    prompt: {
      type: ['string', 'array'],
      items: {
        type: 'string',
      },
      description:
        'The prompt(s) to generate completions for, encoded as a string, array of strings, array of tokens, or array of token arrays.',
    },
    best_of: {
      type: 'integer',
      description:
        "Generates best_of completions server-side and returns the 'best' completion with the highest log probability per token. Must be greater than n when used with n.",
      minimum: 1,
      default: 1,
    },
    echo: {
      type: 'boolean',
      description: 'Echo back the prompt in addition to the completion.',
      default: false,
    },
    frequency_penalty: {
      type: 'number',
      description:
        "Penalty for new tokens based on their existing frequency in the text so far, decreasing the model's likelihood to repeat the same line verbatim.",
      minimum: -2.0,
      maximum: 2.0,
      default: 0,
    },
    logit_bias: {
      type: 'object',
      additionalProperties: {
        type: 'integer',
        minimum: -100,
        maximum: 100,
      },
      description:
        'Modify the likelihood of specified tokens appearing in the completion. A JSON object that maps token IDs to an associated bias value.',
    },
    logprobs: {
      type: 'integer',
      description:
        'Include the log probabilities on the logprobs most likely output tokens, as well as the chosen tokens.',
      maximum: 5,
      default: null,
    },
    max_tokens: {
      type: 'integer',
      description:
        "The maximum number of tokens that can be generated in the completion. The token count of your prompt plus max_tokens can't exceed the model's context length.",
      default: 16,
    },
    n: {
      type: 'integer',
      description: 'How many completions to generate for each prompt. n should be less than or equal to best_of.',
      default: 1,
    },
    presence_penalty: {
      type: 'number',
      description:
        "Penalty for new tokens based on whether they appear in the text so far, increasing the model's likelihood to talk about new topics.",
      minimum: -2.0,
      maximum: 2.0,
      default: 0,
    },
    seed: {
      type: 'integer',
      description:
        'If specified, our system will attempt to make a deterministic sampling, meaning repeated requests with the same seed and parameters should return the same result.',
    },
    stop: {
      type: ['string', 'array'],
      items: {
        type: 'string',
      },
      description:
        "Up to four sequences where the API will stop generating further tokens. The returned text won't contain the stop sequence.",
    },
    stream: {
      type: 'boolean',
      description: 'Whether to stream back partial progress. Tokens will be sent as data-only server-sent events.',
      default: false,
    },
    suffix: {
      type: 'string',
      description:
        'The suffix that comes after a completion of inserted text. Only supported for gpt-3.5-turbo-instruct.',
    },
    temperature: {
      type: 'number',
      description:
        'Sampling temperature to use, between 0 and 2. Higher values make the output more random, while lower values make it more focused and deterministic.',
      minimum: 0,
      maximum: 2,
      default: 1,
    },
    top_p: {
      type: 'number',
      description:
        'An alternative to temperature-based sampling, where the model considers the results of tokens with top_p probability mass.',
      minimum: 0,
      maximum: 1,
      default: 1,
    },
    user: {
      type: 'string',
      description: 'A unique identifier representing the end-user to help monitor and detect abuse.',
    },
  },
  required: ['prompt'],
  additionalProperties: false,
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const azureSchemaProperties: DeepReadonly<JSONSchema7> = azureJsonSchema;

export const retrieveAzureParameters = (): string[] => Object.keys(azureJsonSchema.properties);
